import React, { useMemo } from "react";
import { Download } from "lucide-react";
import DashboardDataTable from "./table/DashboardDataTable";
import { IncrementArrow } from "../AdminIcons";
import { useNavigate } from "react-router-dom";
import { useGetAllEnrollmentsQuery } from "../../services/enrollment/enrollmentApi";

const PAGE_SIZE = 10;

const parseAmount = (amount = "") =>
  Number(String(amount).replace(/[^\d.]/g, "")) || 0;

const formatCurrency = (value) =>
  `Rs${value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const getInitials = (fullName = "") => {
  const parts = fullName.split(" ").filter(Boolean);

  return parts
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
};

const PaymentOverviewSection = () => {
  const navigate = useNavigate();
  const { data: apiResponse } = useGetAllEnrollmentsQuery({
    page: 1,
    limit: PAGE_SIZE,
  });

  const transactions = useMemo(() => {
    if (!Array.isArray(apiResponse?.data)) {
      return [];
    }

    return apiResponse.data.map((item) => {
      const createdAt = item.createdAt ? new Date(item.createdAt) : null;
      const studentName = [item.user?.firstName, item.user?.lastName]
        .filter(Boolean)
        .join(" ");
      const status = item.enrollmentStatus || "Pending";

      return {
        id: item._id,
        invoicId:item.invoiceNumber,
        date: createdAt
          ? createdAt.toLocaleDateString(undefined, {
              month: "long",
              day: "numeric",
              year: "numeric",
            })
          : "N/A",
        course: item.course?.courseTitle || "N/A",
        student: {
          name: studentName ,
          initials:  getInitials(studentName  || "NA"),
          imageUrl: item.user?.profilePicture?.secureUrl ? item.user.profilePicture.secureUrl : null,
        },
        amount: `Rs${Number(item.course?.coursePrice || 0).toFixed(2)}`,
        status,
      };
    });
  }, [apiResponse]);

  const { totalTransactions, pendingTransactions, pendingAmount } = useMemo(() => {
    const total = transactions.reduce(
      (sum, transaction) => sum + parseAmount(transaction.amount),
      0,
    );

    const pending = transactions.filter(
      (transaction) => transaction.status?.toLowerCase() === "pending",
    );

    const pendingTotal = pending.reduce(
      (sum, transaction) => sum + parseAmount(transaction.amount),
      0,
    );

    return {
      totalTransactions: formatCurrency(total),
      pendingTransactions: pending,
      pendingAmount: pendingTotal.toFixed(2),
    };
  }, [transactions]);

  const columns = [
    {
      key: "id",
      label: "TRANSACTION ID",
      width: "12%",
      render: (row) => <span>{row.id}</span>,
    },
    {
      key: "date",
      label: "DATE",
      width: "15%",
      render: (row) => <span>{row.date}</span>,
    },
    {
      key: "course",
      label: "COURSE",
      width: "22%",
      render: (row) => <span>{row.course}</span>,
    },
    {
      key: "student",
      label: "STUDENT",
      width: "20%",
      render: (row) => (
        <div className="flex items-center gap-2">
         
           <img src= {row.student.imageUrl} alt={row.student.name} className="w-7 h-7 rounded-full"/>
         
          <span>{row.student.name}</span>
        </div>
      ),
    },
    {
      key: "amount",
      label: "AMOUNT",
      width: "18%",
      render: (row) => <span>{row.amount}</span>,
    },
    {
      key: "status",
      label: "STATUS",
      width: "16%",
      render: (row) => {
        const statusStyles = {
          completed: "bg-[#DCFCE7] text-[#166534]",
          pending: "bg-[#FEF3C7] text-[#92400E]",
          failed: "bg-[#FEE2E2] text-[#991B1B]",
        };
        const statusClass = statusStyles[row.status] || statusStyles.completed;

        return (
          <span
            className={`capitalize inline-block px-3 py-1 text-xs font-medium rounded-full ${statusClass}`}
          >
            {row.status}
          </span>
        );
      },
    },
  ];

  const rows = transactions.slice(0,4).map((tx) => ({
    id: tx.invoicId,
    date: tx.date,
    course: tx.course,
    student: tx.student,
    amount: tx.amount,
    status: tx.status,
  }));


  const exportToCsv = (items) => {
    const headers = ["Transaction ID", "Date", "Course", "Student", "Amount", "Status"];

    const escape = (value) => {
      if (value === null || typeof value === "undefined") return "";
      const str = String(value);
      return `"${str.replace(/"/g, '""')}"`;
    };

    const rows = (items || []).map((tx) => {
      const id = tx.invoicId || tx.id || "";
      const studentName = tx.student?.name || "";
      return [id, tx.date || "", tx.course || "", studentName, tx.amount || "", tx.status || ""];
    });

    const csvContent = [headers, ...rows]
      .map((r) => r.map(escape).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const filename = `payments_${new Date().toISOString().slice(0,10)}.csv`;
    a.href = url;
    a.setAttribute("download", filename);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <section className="px-5  md:pl-6 py-5 sm:py-10  ">
      <div>
        <h2 className="text-lg sm:text-xl font-bold text-[#111827]">Payment Overview</h2>
      </div>

      <div className="bg-white mt-4  p-4 sm:p-6 rounded-xl shadow-[0_1px_2px_#0000000D]">
        <div className="sm:flex  justify-between items-center">
          <div className="mb-6 flex justify-between sm:justify-center  gap-4 sm:gap-8 ">
            <div>
              <p className="text-xs sm:text-sm  text-[#6B7280]">Total Transactions</p>
              <p className="text-xl sm:text-2xl font-bold text-[#111827]">{totalTransactions}</p>
             
            </div>

            <div className="hidden sm:block h-10 w-px bg-[#E5E7EB]" />

            <div>
              <p className=" text-xs sm:text-sm  text-[#6B7280]">Pending Payments</p>
              <p className="text-xl sm:text-2xl font-bold text-[#F59E0B]">Rs{pendingAmount}</p>
              <p className=" text-xs  text-[#6B7280]">{pendingTransactions.length} transactions</p>
            </div>
          </div>
          <div className="flex  items-center gap-3 pr-4">
            <button
              type="button"
              onClick={() => exportToCsv(transactions)}
              className="flex items-center bg-[#F3F4F6] gap-1 px-3 py-1 sm:py-1.5 text-xs sm:text-sm  text-[#374151] hover:bg-gray-50 rounded transition border "
            >
              <Download className="size-[14px] sm:size-[18px]" />
              Export
            </button>
            <button
              type="button"
              onClick={() => navigate('/admin/purchases')}
              className="px-[18.5px] py-1 sm:py-1.5 bg-[#923D8C] text-white  text-xs sm:text-sm rounded hover:bg-[#7d3277] transition"
            >
              View All
            </button>
          </div>
        </div>

        <DashboardDataTable
          tableClassName="bg-white"
          columns={columns}
          rows={rows}
          headerRowClassName="border-b-0"
        />
      </div>
    </section>
  );
};

export default PaymentOverviewSection;
